'use client';

import { CalculatorValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface CalculatorInputsProps {
  values: CalculatorValues;
  onValuesChange: (field: keyof CalculatorValues, value: number | string) => void;
  translations: ToolsTranslations['savings'];
}

export default function CalculatorInputs({ values, onValuesChange, translations }: CalculatorInputsProps) {
  // Get locale based on currency
  const getLocale = (currency: string) => {
    switch (currency) {
      case '£': return 'en-GB';
      case '$': return 'en-US';
      case '€': return 'de-DE';
      case 'CHF': return 'de-CH';
      case 'kr': 
        // For 'kr' we need to determine if it's SEK, NOK, or DKK based on context
        // Since we can't distinguish between them by symbol alone, default to Swedish
        return 'sv-SE';
      default: return 'sv-SE';
    }
  };

  const locale = getLocale(values.currency);

  // Handle slider changes
  const handleSliderChange = (field: keyof CalculatorValues, value: number) => {
    onValuesChange(field, value);
  };

  // Handle input changes
  const handleInputChange = (field: keyof CalculatorValues, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onValuesChange(field, numValue);
    }
  };

  return (
    <div className="space-y-6">

      {/* Monthly Savings */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="monthly-savings-input" className="text-gray-700 font-medium">{translations.monthlySavings}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="monthly-savings-input"
              type="text"
              value={values.monthlySavings.toLocaleString(locale)}
              onChange={(e) => handleInputChange('monthlySavings', e.target.value.replace(/\s/g, ''))}
              className="w-20 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.monthlySavings}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <input
            id="monthly-savings-slider"
            type="range"
            min="100"
            max="10000"
            step="100"
            value={values.monthlySavings}
            onChange={(e) => handleSliderChange('monthlySavings', parseInt(e.target.value))}
            className="w-full h-2 cursor-pointer"
            aria-label={`${translations.monthlySavings} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>100 {values.currency}</span>
          <span>10 000 {values.currency}</span>
        </div>
      </div>

      {/* Starting Amount */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="starting-amount-input" className="text-gray-700 font-medium">{translations.startingAmount}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="starting-amount-input"
              type="text"
              value={values.startingAmount.toLocaleString(locale)}
              onChange={(e) => handleInputChange('startingAmount', e.target.value.replace(/\s/g, ''))}
              className="w-20 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.startingAmount}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <input
            id="starting-amount-slider"
            type="range"
            min="0"
            max="100000"
            step="1000"
            value={values.startingAmount}
            onChange={(e) => handleSliderChange('startingAmount', parseInt(e.target.value))}
            className="w-full h-2 cursor-pointer"
            aria-label={`${translations.startingAmount} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>0 {values.currency}</span>
          <span>100 000 {values.currency}</span>
        </div>
      </div>

      {/* Savings Period */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="savings-period-input" className="text-gray-700 font-medium">{translations.savingsPeriod}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="savings-period-input"
              type="number"
              value={values.savingsPeriod}
              onChange={(e) => handleInputChange('savingsPeriod', e.target.value)}
              className="w-20 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.savingsPeriod}
            />
            <span className="text-gray-800 font-semibold">{translations.yearsSuffix}</span>
          </div>
        </div>
        <div className="relative">
          <input
            id="savings-period-slider"
            type="range"
            min="1"
            max="30"
            step="1"
            value={values.savingsPeriod}
            onChange={(e) => handleSliderChange('savingsPeriod', parseInt(e.target.value))}
            className="w-full h-2 cursor-pointer"
            aria-label={`${translations.savingsPeriod} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.yearsSuffix}</span>
          <span>30 {translations.yearsSuffix}</span>
        </div>
      </div>

      {/* Expected Return */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="expected-return-input" className="text-gray-700 font-medium">{translations.expectedReturn}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="expected-return-input"
              type="number"
              value={values.expectedReturn}
              onChange={(e) => handleInputChange('expectedReturn', e.target.value)}
              className="w-20 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.expectedReturn}
            />
            <span className="text-gray-800 font-semibold">{translations.percentSuffix}</span>
          </div>
        </div>
        <div className="relative">
          <input
            id="expected-return-slider"
            type="range"
            min="1"
            max="15"
            step="0.5"
            value={values.expectedReturn}
            onChange={(e) => handleSliderChange('expectedReturn', parseFloat(e.target.value))}
            className="w-full h-2 cursor-pointer"
            aria-label={`${translations.expectedReturn} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1%</span>
          <span>15%</span>
        </div>
      </div>

      <style jsx>{``}</style>
    </div>
  );
}
